import React from 'react';
import { useTranslation } from 'react-i18next';
import RequiredTag from 'shared/components/forms/RequiredTag';
import { Transform } from './types';
import { valAt, applyTransformsJs, formatPreviewText } from './helpers';
import { TransformAddButton, TransformPills } from './TransformControls';
import { MappingChip, EmptySlot } from './SlotParts';
import { SLOT_LABEL, SLOT_ROW } from './styles';
import { DragPathRef, StartPick, useDragOver } from './dragAndDrop';

function DropSlot({
  label,
  mapping,
  sampleInput,
  onDrop,
  onClear,
  readOnly,
  dragPathRef,
  required,
  onStartPick,
  sourceArmed,
  transforms,
  onChangeTransforms,
  transformsLocked,
}: {
  label: string;
  mapping: string[] | null;
  sampleInput: any;
  onDrop: (path: string[]) => void;
  onClear: () => void;
  readOnly: boolean;
  dragPathRef: DragPathRef;
  required?: boolean;
  onStartPick?: StartPick;
  // A source leaf is armed for click-mapping — highlight the empty zone as a hot target.
  sourceArmed?: boolean;
  transforms?: Transform[];
  onChangeTransforms?: (next: Transform[]) => void;
  // Transform edits regenerate jq; while the filter is complex the builder never pushes
  // regenerated jq to the parent, so editing here would silently not save.
  transformsLocked?: boolean;
}) {
  const { isOver, handlers, resetOver } = useDragOver(readOnly);
  const previewValue = mapping ? valAt(sampleInput, mapping) : undefined;
  const pathNotInSample = mapping && previewValue === undefined;
  // Chip shows the RAW source value; the transformed result renders after the pills,
  // so the row reads before → after.
  const transformedValue = applyTransformsJs(
    previewValue,
    transforms,
    sampleInput,
  );
  const { t } = useTranslation();

  const wand =
    !readOnly && onChangeTransforms ? (
      <TransformAddButton
        onAdd={(tr) => onChangeTransforms([...(transforms ?? []), tr])}
        disabled={transformsLocked}
        // Only an empty slot restricts the kinds: with no base value, just a
        // hardcoded default or a fallback field can supply one.
        allowedKinds={
          mapping || transforms?.some((tr) => tr.kind === 'coalesce')
            ? undefined
            : ['default', 'coalesce']
        }
      />
    ) : undefined;

  return (
    <div data-slot-row tabIndex={-1} className={SLOT_ROW}>
      <span className={SLOT_LABEL}>
        {label}
        {required && <RequiredTag size="xs" />}
      </span>
      {mapping ? (
        <MappingChip
          path={mapping}
          previewValue={previewValue}
          notInSample={!!pathNotInSample}
          readOnly={readOnly}
          onClear={onClear}
          clearLabel={t('Clear mapping')}
          wand={wand}
        />
      ) : (
        <EmptySlot
          label={t('Drop a source field')}
          isOver={isOver}
          sourceArmed={!!sourceArmed}
          readOnly={readOnly}
          handlers={handlers}
          onActivate={(el) => onStartPick?.(onDrop, el)}
          onDropPath={() => {
            resetOver();
            if (dragPathRef.current) onDrop(dragPathRef.current);
          }}
          wand={wand}
        />
      )}
      {transforms && transforms.length > 0 && onChangeTransforms && (
        <TransformPills
          transforms={transforms}
          onChange={onChangeTransforms}
          readOnly={readOnly || transformsLocked}
          previewText={formatPreviewText(transformedValue)}
        />
      )}
    </div>
  );
}

export default DropSlot;
